import PropTypes from "prop-types"
import { useState } from "react"
import { Link } from "react-router-dom"

import Textarea from "components/landing/textarea"
import Button from "components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "components/ui/carousel"
import { cn } from "styles/utils"

import ProductCard from "./landing-product/product-card"

const LandingTabbedCarousel = ({ element }) => {
  const { data, super_filters } = element || {}
  const { classNames, heading, sections, type } = data || {}
  const [tab, setTab] = useState(super_filters?.[0])

  const currentTab = sections ? (sections[tab.key] ?? {}) : {}

  return (
    <div
      className={cn(
        "desktop:py-60 py-32",
        element?.contain && "contained-wide",
        classNames?.blockClassName
      )}
    >
      <div className={cn("text-center", classNames?.headingContainerClassName)}>
        {element.textarea ? (
          <Textarea
            element={element.textarea}
            linkClickData={element.anchor_name || element.id}
            style={{
              id: `${element.anchor_name || element.id}-heading`,
              ...element.textarea.style,
            }}
          />
        ) : heading ? (
          <h2
            className={cn("h2", classNames?.headingClassName)}
            dangerouslySetInnerHTML={{ __html: heading }}
            id={`${element.anchor_name || element.id}-heading`}
          />
        ) : null}
        <div
          className={cn(
            "desktop:my-40 tablet:justify-between my-24 flex justify-center px-16",
            classNames?.tabContainerClassName
          )}
        >
          <ul
            {...((element.textarea || heading) && {
              "aria-labelledby": `${element.anchor_name || element.id}-heading`,
            })}
            className={cn(
              "tablet:gap-32 flex list-none items-center gap-12",
              classNames?.tabListClassName
            )}
            role="tablist"
          >
            {(super_filters || []).map((filter) => (
              <li key={`${element.id}-tab-${filter.key}`} role="presentation">
                <Button
                  aria-controls={`${element.anchor_name || element.id}-${tab.key}-tabpanel`}
                  aria-selected={tab.key === filter.key}
                  className={cn(
                    "body-lg leading-36 relative font-semibold uppercase",
                    classNames?.tabClassName
                  )}
                  id={`${element.anchor_name || element.id}-${tab.key}-tab`}
                  onClick={() => setTab(filter)}
                  role="tab"
                  size="sm"
                  variant="tertiary"
                >
                  <span
                    className={cn(
                      "border-b-4 border-b-transparent",
                      tab.key === filter.key && "border-b-primary",
                      classNames?.tabSpanClassName
                    )}
                  >
                    {filter.name}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
          {currentTab.cta && currentTab.url && (
            <div className="tablet:block hidden">
              <Button asChild className="uppercase" variant="primary">
                <Link to={currentTab.url}>{currentTab.cta}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
      {type === "grid" ? (
        <div
          className={cn(
            "tablet:grid-cols-4 tablet:gap-24 grid grid-cols-2 gap-x-12 gap-y-24 px-16",
            classNames?.gridClassName
          )}
        >
          {(currentTab.products || []).map((product, i) => (
            <ProductCard
              element={element}
              imageSizes={
                classNames?.imageSizes ||
                (element?.contain
                  ? "(min-width: 1920px) 424px, (min-width: 768px) 25vw, 50vw"
                  : "(min-width: 768px) 25vw, 50vw")
              }
              index={i}
              key={`${tab.key}-${product.style_code}-${i}`}
              product={product}
            />
          ))}
        </div>
      ) : (
        <Carousel
          aria-labelledby={`${element.anchor_name || element.id}-${tab.key}-tab`}
          className={classNames?.carouselClassName}
          description={`${tab.name} Carousel`}
          id={`${element.anchor_name || element.id}-${tab.key}-tabpanel`}
          opts={{ dragFree: true, loop: false }}
          role="tabpanel"
        >
          <CarouselContent
            className={classNames?.carouselContentClassName}
            viewportClassName={cn(
              "tablet:mask-fade-right",
              classNames?.viewportClassName
            )}
          >
            {(currentTab.products || []).map((product, i) => (
              <CarouselItem
                className={cn(
                  "desktop:basis-[21%] tablet:basis-3/10 desktop:pl-32 tablet:min-w-0 min-w-166 basis-[45%] pl-16",
                  classNames?.carouselItemClassName
                )}
                key={`${tab.key}-${product.style_code}-${i}`}
              >
                <ProductCard
                  element={element}
                  imageSizes={
                    classNames?.imageSizes ||
                    "(max-width: 1023px) 40vw, (max-width: 1919px) 21vw, 346px"
                  }
                  index={i}
                  product={product}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="tablet:inline-flex" />
          <CarouselNext className="tablet:inline-flex" />
        </Carousel>
      )}
      {currentTab.cta && currentTab.url && (
        <div className="tablet:hidden text-center">
          <Button
            asChild
            className="widescreen:mt-60 mx-auto mt-32 uppercase"
            variant="primary"
          >
            <Link to={currentTab.url}>{currentTab.cta}</Link>
          </Button>
        </div>
      )}
    </div>
  )
}

LandingTabbedCarousel.propTypes = {
  element: PropTypes.object,
}

export default LandingTabbedCarousel
